import {
  type ClipColor,
  colorMatrix,
  colorMatrixKey,
  hasColorMatrix,
  svgColorMatrixValues,
  vignetteStops,
} from "@cva/shared";

/**
 * Colour corrections in the editor: one SVG `feColorMatrix` per matrix in a
 * hidden SVG of the page, referenced by the canvas player (`ctx.filter`) and
 * by previews (CSS `filter`). The export applies the same matrix with ffmpeg.
 */

const ROOT_ID = "cva-color-filters";
const SVG_NS = "http://www.w3.org/2000/svg";

function root(): SVGSVGElement {
  let svg = document.getElementById(ROOT_ID) as SVGSVGElement | null;
  if (!svg) {
    svg = document.createElementNS(SVG_NS, "svg");
    svg.id = ROOT_ID;
    svg.setAttribute("aria-hidden", "true");
    // Not display:none – filters inside it would stop working.
    svg.setAttribute("style", "position:absolute;width:0;height:0;overflow:hidden");
    document.body.appendChild(svg);
  }
  return svg;
}

/** `url(#…)` of the clip's colour matrix, or null when the colours stay as they are. */
export function colorFilterUrl(color: ClipColor | undefined): string | null {
  if (!hasColorMatrix(color)) return null;
  const matrix = colorMatrix(color);
  const id = `cva-grade-${colorMatrixKey(matrix)}`;
  if (!document.getElementById(id)) {
    const filter = document.createElementNS(SVG_NS, "filter");
    filter.id = id;
    filter.setAttribute("color-interpolation-filters", "sRGB");
    const node = document.createElementNS(SVG_NS, "feColorMatrix");
    node.setAttribute("type", "matrix");
    node.setAttribute("values", svgColorMatrixValues(matrix));
    filter.appendChild(node);
    root().appendChild(filter);
  }
  return `url(#${id})`;
}

/** Darkens the corners of `area` like the export's vignette (elliptical gradient). */
export function drawVignette(
  ctx: CanvasRenderingContext2D,
  area: { x: number; y: number; width: number; height: number },
  strength: number | undefined,
) {
  const stops = vignetteStops(strength);
  if (stops.length === 0 || area.width < 1 || area.height < 1) return;
  ctx.save();
  ctx.translate(area.x + area.width / 2, area.y + area.height / 2);
  ctx.scale(area.width / 2, area.height / 2);
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, Math.SQRT2);
  for (const stop of stops) gradient.addColorStop(stop.offset, `rgba(0, 0, 0, ${stop.alpha})`);
  ctx.fillStyle = gradient;
  ctx.fillRect(-1, -1, 2, 2);
  ctx.restore();
}
